/**
 * Shared Zod schema + helpers for the Event Type Add/Edit forms.
 *
 * - hasBlockedWord: word-boundary-aware blocked-word check for reference-field names.
 * - rowsToJsonSchema / jsonSchemaToRows: reference-field rows <-> JSON Schema (data.reference).
 * - deriveUri: prefill the cnx:// URI from the event type name.
 * - eventTypeBase / addEventTypeSchema / editEventTypeSchema: validation.
 *
 * Selects emit strings, so entity/action use z.coerce.number().
 */

import { z } from 'zod'
import { BLOCKED_FIELD_WORDS, EVENT_STATUSES, REFERENCE_FIELD_TYPES } from '../eventConstants'
import type { ReferenceFieldRow } from '@/types/events'

// Split camelCase into words; block when any run of 1-3 adjacent words joins to a blocked word.
// e.g. nwkSKey -> nwk,s,key -> "nwkskey" (blocked); tokenCount -> token (blocked); stockenValue -> ok.
export function hasBlockedWord(name: string): boolean {
  const words = name.split(/(?=[A-Z])/).map((w) => w.toLowerCase())
  return words.some((_, i) => [1, 2, 3].some((n) => BLOCKED_FIELD_WORDS.has(words.slice(i, i + n).join(''))))
}

export function rowsToJsonSchema(rows: ReferenceFieldRow[]): Record<string, unknown> {
  return {
    type: 'object',
    properties: Object.fromEntries(
      rows.map((r) => [r.name, { type: r.type, ...(r.description ? { description: r.description } : {}) }]),
    ),
    required: rows.filter((r) => r.required).map((r) => r.name),
    additionalProperties: false,
  }
}

/**
 * Reverse of rowsToJsonSchema. Returns the rows, or `null` when the schema contains anything
 * the simple rows can't represent (nested properties, enum, format, …) so the caller can fall
 * back to a read-only JSON view.
 */
export function jsonSchemaToRows(schema: unknown): ReferenceFieldRow[] | null {
  const s = schema as { type?: string; properties?: Record<string, { type?: string; description?: string }>; required?: string[] } | null
  if (!s || s.type !== 'object' || !s.properties) return []
  const required = new Set<string>(Array.isArray(s.required) ? s.required : [])
  const allowedKeys = new Set(['type', 'description'])
  const rows: ReferenceFieldRow[] = []
  for (const [name, prop] of Object.entries(s.properties)) {
    const keys = Object.keys(prop)
    if (!prop.type || !(REFERENCE_FIELD_TYPES as readonly string[]).includes(prop.type) || keys.some((k) => !allowedKeys.has(k))) {
      return null
    }
    rows.push({ name, type: prop.type as ReferenceFieldRow['type'], required: required.has(name), description: prop.description })
  }
  return rows
}

export function deriveUri(eventTypeName: string): string {
  const kebab = eventTypeName.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()
  return `cnx://997/component/conex-event-type/${kebab}`
}

export const referenceFieldRow = z.object({
  name: z
    .string()
    .regex(/^[a-z][a-zA-Z0-9]*$/, 'camelCase, starts with a lowercase letter')
    .refine((n) => !hasBlockedWord(n), 'Events must not carry credentials, codes or storage keys'),
  type: z.enum(REFERENCE_FIELD_TYPES as unknown as [string, ...string[]]),
  required: z.boolean(),
  description: z.string().max(200).optional(),
})

const uniqueStrings = (label: string) =>
  z.array(z.string()).refine((a) => new Set(a).size === a.length, `Duplicate ${label}`)

export const eventTypeBase = z.object({
  eventTypeLabel: z.string().min(1, 'Label is required').max(150),
  eventTypeDescription: z.string().min(1, 'Description is required').max(500),
  entityTypeNumber: z.coerce.number().refine((n) => n > 0, 'Pick an entity type'),
  actionNumber: z.coerce.number().refine((n) => n > 0, 'Pick an action'),
  publisherUri: z.string().min(1, 'Pick a publisher'),
  subscriberUris: uniqueStrings('subscriber'),
  status: z.enum(EVENT_STATUSES as unknown as [string, ...string[]]),
  referenceFields: z
    .array(referenceFieldRow)
    .refine((a) => new Set(a.map((f) => f.name)).size === a.length, 'Field names must be unique'),
})

export const addEventTypeSchema = eventTypeBase.extend({
  eventTypeName: z.string().regex(/^[a-z][a-zA-Z0-9]*$/, 'camelCase, starts with a lowercase letter'),
  eventTypeUri: z.string().startsWith('cnx://', 'Must start with cnx://'),
})

export const editEventTypeSchema = eventTypeBase.extend({
  eventTypeNumber: z.number(),
})

export type AddEventTypeData = z.infer<typeof addEventTypeSchema>
export type EditEventTypeData = z.infer<typeof editEventTypeSchema>
